import classNames from 'classnames'
import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { ZStack } from '~/components/elements/ZStack'
import { type CustomerStrategySegmentRoute } from '~/gql/graphql'
import {
  findByPath,
  formatNineSegment,
  type NineNumber,
  NumFormat,
} from '~/utils'

import { dataDisplayTypeAtom } from '../../atoms'
import { useSurvey } from '../../hooks/useSurvey'
import { useTabulationData } from '../../hooks/useTabulationQuery'

import { Circle } from './Circle'
import styles from './NineNumberCell.css'

export type NineNumberCellProps = {
  nineNumber: NineNumber
  selectedRoute: CustomerStrategySegmentRoute | null
}

export const NineNumberCell = ({
  nineNumber,
  selectedRoute,
}: NineNumberCellProps) => {
  const { t } = useTranslation()
  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)

  const label = React.useMemo(
    () => formatNineSegment(nineNumber, '%n %t1', t),
    [nineNumber, t],
  )

  const tabulationData = useTabulationData()
  const survey = useSurvey()

  const value = React.useMemo(() => {
    const data = R.pipe(tabulationData, findByPath('nineNumber', nineNumber))
    const count = data?.count ?? NaN
    const ratio = data?.ratio ?? NaN

    const tamNumber = survey?.metadata?.tamNumber ?? 0
    const numberOfTotalAnswers = survey?.numberOfTotalAnswers ?? NaN
    const tamRatio = tamNumber === 0 ? 1 : tamNumber / numberOfTotalAnswers

    return NumFormat.forCount(count, ratio, tamRatio, dataDisplayType)
  }, [
    dataDisplayType,
    nineNumber,
    survey?.metadata?.tamNumber,
    survey?.numberOfTotalAnswers,
    tabulationData,
  ])

  const isActive =
    selectedRoute !== null && R.values(selectedRoute).includes(nineNumber)

  return (
    <ZStack
      className={classNames(
        styles.root,
        styles[`segment-${nineNumber}`],
        isActive && styles.active,
      )}
    >
      <VStack
        align="center"
        backgroundColor="transparent"
        justify="center"
        spacing={0}
      >
        <Circle nineNumber={nineNumber} />
      </VStack>

      <VStack
        align="center"
        backgroundColor="transparent"
        justify="space-between"
        spacing={0}
      >
        <Typography variant="small">{label}</Typography>

        <Typography lineHeight={2.5} variant="small">
          {value}
        </Typography>
      </VStack>
    </ZStack>
  )
}
